import imageCompression from 'browser-image-compression';
import React, { useRef, useState } from 'react';
import { PhotoIcon, XMarkIcon, CameraIcon, FolderIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useLeadFormStore } from '../../../store/leadFormStore';
import { StepProps } from '../types';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

type DocumentCategory = 'interestForm' | 'screenshot' | 'other';

const DocumentsStep: React.FC<StepProps> = ({ onNext, onBack }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCategory, setUploadingCategory] = useState<DocumentCategory | null>(null);
  const { formData, updateFormData } = useLeadFormStore();
  const documents = formData.documents as {
    interestForm: any[];
    screenshot: any[];
    other: any[];
  };

  // Check if device is mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const compressImage = async (file: File): Promise<Blob> => {
    try {
      const options = {
        maxSizeMB: 0.5,          // Reduce to 0.5MB per image
        maxWidthOrHeight: 1280,  // Reduce max dimension
        useWebWorker: true,
        fileType: 'image/jpeg',  // Convert to JPEG
        quality: 0.7             // Reduce quality
      };
      
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      throw error;
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!uploadingCategory) return;

    for (const file of files) {
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        toast.error(`${file.name} is not a supported image type`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Maximum size is 25MB`);
        continue;
      }

      try {
        let processedFile: Blob = file;
        
        // Compress if image is larger than 1MB
        if (file.size > 1 * 1024 * 1024) {
          const toastId = toast.loading('Compressing image...');
          processedFile = await compressImage(file);
          toast.success('Image compressed', { id: toastId });
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          const newDocument = {
            id: Date.now().toString() + Math.random(),
            url: base64,
            type: uploadingCategory,
            name: file.name,
            category: uploadingCategory,
            uploadedAt: new Date()
          };

          const currentDocs = documents[uploadingCategory] || [];
          updateFormData({
            documents: {
              ...documents,
              [uploadingCategory]: [...currentDocs, newDocument]
            }
          });

          toast.success(`${file.name} uploaded successfully`);
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error('Error processing file:', error);
        toast.error(`Failed to process ${file.name}`);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    setUploadingCategory(null);
  };

  const handleRemoveDocument = (category: DocumentCategory, docId: string) => {
    const currentDocs = documents[category] || [];
    updateFormData({
      documents: {
        ...documents,
        [category]: currentDocs.filter((doc: any) => doc.id !== docId)
      }
    });
  };

  const handleCameraClick = (category: DocumentCategory) => {
    setUploadingCategory(category);
    cameraInputRef.current?.click();
  };

  const handleFileClick = (category: DocumentCategory) => {
    setUploadingCategory(category);
    fileInputRef.current?.click();
  };

  const handleNext = () => {
    if (!documents.interestForm?.length || !documents.screenshot?.length) {
      toast.error('Please upload at least one Interest Form and one Screenshot');
      return;
    }
    onNext();
  };

  const renderUploadOptions = (category: DocumentCategory) => {
    if (isMobile) {
      return (
        <>
          <button
            onClick={() => handleCameraClick(category)}
            className="h-32 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
          >
            <CameraIcon className="h-8 w-8 text-gray-400" />
            <span className="mt-2 text-sm text-gray-600">Take Photo</span>
          </button>
          <button
            onClick={() => handleFileClick(category)}
            className="h-32 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
          >
            <FolderIcon className="h-8 w-8 text-gray-400" />
            <span className="mt-2 text-sm text-gray-600">Choose File</span>
          </button>
        </>
      );
    } else {
      return (
        <button
          onClick={() => handleFileClick(category)}
          className="h-32 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
        >
          <PhotoIcon className="h-8 w-8 text-gray-400" />
          <span className="mt-2 text-sm text-gray-600">Add Photo</span>
        </button>
      );
    }
  };

  return (
    <div className="space-y-6 pb-24">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Document Upload</h3>
        <p className="mt-1 text-sm text-gray-600">
          Upload required documents. Interest Forms and Screenshots are mandatory.
        </p>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <input
        ref={cameraInputRef}
        type="file"
        multiple
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Interest Forms */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Interest Forms <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500">Photos of signed interest forms</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {documents.interestForm?.map((doc: any) => (
            <div key={doc.id} className="relative group">
              <img
                src={doc.url}
                alt={doc.name}
                className="h-32 w-full object-cover rounded-lg"
              />
              <button
                onClick={() => handleRemoveDocument('interestForm', doc.id)}
                className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
          
          {renderUploadOptions('interestForm')}
        </div>
      </div>

      {/* Screenshots */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Screenshots <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500">Screenshots of relevant information</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {documents.screenshot?.map((doc: any) => (
            <div key={doc.id} className="relative group">
              <img
                src={doc.url}
                alt={doc.name}
                className="h-32 w-full object-cover rounded-lg"
              />
              <button
                onClick={() => handleRemoveDocument('screenshot', doc.id)}
                className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
          
          {renderUploadOptions('screenshot')}
        </div>
      </div>

      {/* Other Documents */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Other Documents (Optional)
          </label>
          <p className="text-xs text-gray-500">Any additional relevant documents</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {documents.other?.map((doc: any) => (
            <div key={doc.id} className="relative group">
              <img
                src={doc.url}
                alt={doc.name}
                className="h-32 w-full object-cover rounded-lg"
              />
              <button
                onClick={() => handleRemoveDocument('other', doc.id)}
                className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
          
          {renderUploadOptions('other')}
        </div>
      </div>

      {/* Navigation handled by FormNavigation */}
    </div>
  );
};

export default DocumentsStep;